import { Insurely } from "components/Signup/store/state/types/insurely";
import { isFuture, isSameDay } from "date-fns";
import { formatCurrency } from "../../../../../utils/utils";

export const getCompetitorCopay = (state?: Insurely): string | undefined => {
  if (state?.currentInsurelyPolicyIndex === undefined) {
    return undefined;
  }
  const amount = state.policies?.[
    state.currentInsurelyPolicyIndex
  ].deductibles?.find(
    (deductible) => deductible.deductibleId === "deductibleVariable"
  )?.deductibleAmount;

  return amount ? `${amount}%` : undefined;
};

export const getCompetitorDeductible = (
  state?: Insurely
): string | undefined => {
  if (state?.currentInsurelyPolicyIndex === undefined) {
    return undefined;
  }
  const amount = state.policies?.[
    state.currentInsurelyPolicyIndex
  ].deductibles?.find(
    (deductible) => deductible.deductibleId === "deductibleFixed"
  )?.deductibleAmount;

  return amount ? formatCurrency(amount) : undefined;
};

// if the user has changed the start date, we can't cancel the previous insurance so we skip to payment step
// trygg hansa users can't cancel their insurance so we skip flytthjälpen step
// 2024-02-16: Insurly has problems with hedvig, disabling flytthjälpen for hedvig.
export const isCancellationAllowedByCompetitor = (state?: Insurely) => {
  const name =
    state?.policies?.[state.currentInsurelyPolicyIndex || 0].insurance
      .insuranceCompany;

  return !["se-trygg-hansa", "se-hedvig"].includes(name ?? "");
};

export const getCompetitorName = (state?: Insurely): string | undefined => {
  const name =
    state?.policies?.[state.currentInsurelyPolicyIndex || 0].insurance
      .insuranceCompany;

  switch (name) {
    case "se-agria": {
      return "Agria";
    }
    case "se-lansforsakringar": {
      return "Länsförsäkringar";
    }
    case "se-trygg-hansa": {
      return "Trygg Hansa";
    }
    case "se-folksam": {
      return "Folksam";
    }
    case "se-if": {
      return "IF";
    }
    case "se-bliwa": {
      return "Bliwa";
    }
    case "se-moderna": {
      return "Moderna Försäkringar";
    }
    case "se-dina": {
      return "Dina Försäkringar";
    }
    case "se-svedea": {
      return "Svedea";
    }
    case "se-skandia": {
      return "Skandia";
    }
    case "se-ica": {
      return "ICA Försäkring";
    }
    case "se-sveland": {
      return "Sveland";
    }
    case "se-hedvig": {
      return "Hedvig";
    }
    case "se-demo": {
      return "Demo";
    }
    case "se-manypets": {
      return "Manypets";
    }
    default:
      return undefined;
  }
};

export const getCompetitorYearlyAmount = (
  state: Insurely
): string | undefined => {
  if (state.currentInsurelyPolicyIndex === undefined) {
    return undefined;
  }
  const premiumAmountYearRounded =
    state.policies?.[state.currentInsurelyPolicyIndex].insurance
      .premiumAmountYearRounded;

  return formatCurrency(premiumAmountYearRounded) || undefined;
};

export const getCompetitorMonthlyAmount = (
  state: Insurely
): string | undefined => {
  if (state.currentInsurelyPolicyIndex === undefined) {
    return undefined;
  }
  const premiumAmountYearRounded =
    state.policies?.[state.currentInsurelyPolicyIndex].insurance
      .premiumAmountYearRounded;

  return premiumAmountYearRounded
    ? formatCurrency(Math.round(premiumAmountYearRounded / 12))
    : undefined;
};

export const getCompetitorVetCareAmount = (
  state: Insurely
): string | undefined => {
  if (state.currentInsurelyPolicyIndex === undefined) {
    return undefined;
  }
  const vetCareAmount =
    state.policies?.[state.currentInsurelyPolicyIndex].insurance
      .veterinaryCareAmount;

  return vetCareAmount ? formatCurrency(vetCareAmount) : undefined;
};

export const getCompetitorRenewalDate = (
  state?: Insurely
): string | undefined => {
  if (state?.currentInsurelyPolicyIndex === undefined) {
    return undefined;
  }
  const insurelyInsurance =
    state.policies?.[state.currentInsurelyPolicyIndex].insurance;

  if (!insurelyInsurance) {
    return undefined;
  }

  if (insurelyInsurance.coming) {
    return insurelyInsurance.startDate;
  }

  const startDateIsToday = isSameDay(
    new Date(),
    new Date(insurelyInsurance.startDate)
  );

  if (startDateIsToday) {
    return insurelyInsurance.startDate;
  }

  if (!isFuture(new Date(insurelyInsurance.renewalDate))) {
    return undefined;
  }

  return insurelyInsurance.renewalDate;
};

export const getCompetitorInsuranceNumber = (
  state?: Insurely
): string | undefined => {
  if (state?.currentInsurelyPolicyIndex === undefined) {
    return undefined;
  }
  const insuranceNumber =
    state.policies?.[state.currentInsurelyPolicyIndex].insurance
      .insuranceNumber;

  return insuranceNumber;
};

export const isManyPetsMultipleAnimals = (state: Insurely): boolean => {
  const competitorName = getCompetitorName(state);
  if (competitorName !== "Manypets") {
    return false;
  }

  const insuranceNumber = getCompetitorInsuranceNumber(state);
  if (!insuranceNumber) {
    return false;
  }

  const mpInsurances = state.policies?.filter(
    (policy) => policy.insurance.insuranceCompany === "se-manypets"
  );

  const insuranceNrAndCount = mpInsurances?.reduce((acc, policy) => {
    const insuranceNr = policy.insurance.insuranceNumber;
    const count = acc[insuranceNr] ? acc[insuranceNr] + 1 : 1;
    return {
      ...acc,
      [insuranceNr]: count,
    };
  }, {} as any);

  return insuranceNrAndCount?.[insuranceNumber] > 1;
};
